import { getSupabase } from 'shared/supabase/client';

export type ActivityLogItem = {
  id: string;
  action: string;
  summary: string | null;
  app: string | null;
  actorRole: string | null;
  institutionId: string | null;
  resourceType: string | null;
  resourceId: string | null;
  metadata: Record<string, unknown>;
  severity: string;
  createdAt: string;
};

export type LogActivityInput = {
  action: string;
  summary?: string;
  app?: 'ai' | 'school' | 'ops';
  actorRole?: string;
  institutionId?: string;
  targetUserId?: string;
  resourceType?: string;
  resourceId?: string;
  metadata?: Record<string, unknown>;
  severity?: 'info' | 'security' | 'billing';
};

function mapRow(row: Record<string, unknown>): ActivityLogItem {
  return {
    id: row.id as string,
    action: row.action as string,
    summary: (row.summary as string | null) ?? null,
    app: (row.app as string | null) ?? null,
    actorRole: (row.actor_role as string | null) ?? null,
    institutionId: (row.institution_id as string | null) ?? null,
    resourceType: (row.resource_type as string | null) ?? null,
    resourceId: (row.resource_id as string | null) ?? null,
    metadata: (row.metadata as Record<string, unknown>) ?? {},
    severity: (row.severity as string) ?? 'info',
    createdAt: row.created_at as string,
  };
}

export function logActivity(input: LogActivityInput): void {
  void (async () => {
    try {
      const supabase = getSupabase();
      await supabase.rpc('core_log_activity', {
        p_action: input.action,
        p_summary: input.summary ?? null,
        p_app: input.app ?? 'ai',
        p_actor_role: input.actorRole ?? 'sadhak',
        p_institution_id: input.institutionId ?? null,
        p_target_user_id: input.targetUserId ?? null,
        p_resource_type: input.resourceType ?? null,
        p_resource_id: input.resourceId ?? null,
        p_metadata: input.metadata ?? {},
        p_severity: input.severity ?? 'info',
        p_impersonation: false,
      });
    } catch {
      /* non-blocking */
    }
  })();
}

export async function listMyActivityLogs(
  limit = 25,
  before?: string
): Promise<ActivityLogItem[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('core_list_my_activity_logs', {
    p_limit: limit,
    p_before: before ?? null,
  });
  if (error) throw new Error(error.message);
  const payload = data as { ok: boolean; items?: Record<string, unknown>[]; error?: string };
  if (!payload?.ok) throw new Error(payload.error ?? 'Failed to load activity');
  return (payload.items ?? []).map((row) => mapRow(row));
}

export function activityLogsToCsv(items: ActivityLogItem[]): string {
  const header = ['created_at', 'action', 'summary', 'app', 'severity'];
  const lines = items.map((item) =>
    [
      item.createdAt,
      item.action,
      (item.summary ?? '').replace(/"/g, '""'),
      item.app ?? '',
      item.severity,
    ]
      .map((v) => `"${v}"`)
      .join(',')
  );
  return [header.join(','), ...lines].join('\n');
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Excel-compatible export (UTF-8 BOM + .xls) without a spreadsheet library. */
export function downloadExcel(filename: string, csv: string): void {
  const base = filename.replace(/\.(csv|xls|xlsx)$/i, '');
  const blob = new Blob(['\uFEFF', csv], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${base}.xls`;
  anchor.click();
  URL.revokeObjectURL(url);
}
